"use client";
import { ComboboxItemCheckedContext, ComboboxItemValueContext, ComboboxListRoleContext, useComboboxScopedContext } from "./combobox-context.js";
import { useCompositeItem } from "../composite/composite-item.js";
import { useCompositeHover } from "../composite/composite-hover.js";
import { createElement, createHook, forwardRef, memo, useBooleanEvent, useEvent, useWrapElement } from "@ariakit/react-utils";
import { getItemRoleByPopupRole, hasFocus, invariant, isApple, isDownloading, isOpeningInNewTab, isTextField } from "@ariakit/utils";
import { useCallback, useContext } from "react";
import { jsx } from "react/jsx-runtime";
import { useStoreStateObject } from "@ariakit/react-store";
//#region src/combobox/combobox-item.tsx
const TagName = "div";
function isSelected(storeValue, itemValue) {
	if (itemValue == null) return;
	if (storeValue == null) return false;
	if (Array.isArray(storeValue)) return storeValue.includes(itemValue);
	return storeValue === itemValue;
}
/**
* Returns the role for a combobox item based on the popup role.
*/
function getItemRole(popupRole) {
	return getItemRoleByPopupRole(popupRole) ?? "option";
}
/**
* Returns props to create a `ComboboxItem` component.
* @see https://ariakit.com/components/combobox
* @example
* ```jsx
* const store = useComboboxStore();
* const props = useComboboxItem({ store, value: "value" });
* <Role {...props} />
* ```
*/
const useComboboxItem = createHook(function useComboboxItem({ store, value, hideOnClick, setValueOnClick, selectValueOnClick = true, resetValueOnSelect, focusOnHover = false, moveOnKeyPress = true, getItem: getItemProp, ...props }) {
	const context = useComboboxScopedContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "ComboboxItem must be wrapped in a ComboboxList or ComboboxPopover component.");
	const { resetValueOnSelectState, multiSelectable, selected } = useStoreStateObject(store, {
		resetValueOnSelectState: "resetValueOnSelect",
		multiSelectable(state) {
			return Array.isArray(state.selectedValue);
		},
		selected(state) {
			return isSelected(state.selectedValue, value);
		}
	});
	const getItem = useCallback((item) => {
		const nextItem = {
			...item,
			value
		};
		if (getItemProp) return getItemProp(nextItem);
		return nextItem;
	}, [value, getItemProp]);
	setValueOnClick = setValueOnClick ?? !multiSelectable;
	hideOnClick = hideOnClick ?? (value != null && !multiSelectable);
	const onClickProp = props.onClick;
	const setValueOnClickProp = useBooleanEvent(setValueOnClick);
	const selectValueOnClickProp = useBooleanEvent(selectValueOnClick);
	const resetValueOnSelectProp = useBooleanEvent(resetValueOnSelect ?? resetValueOnSelectState ?? multiSelectable);
	const hideOnClickProp = useBooleanEvent(hideOnClick);
	const onClick = useEvent((event) => {
		onClickProp?.(event);
		if (event.defaultPrevented) return;
		if (isDownloading(event)) return;
		if (isOpeningInNewTab(event)) return;
		if (value != null) {
			if (selectValueOnClickProp(event)) {
				if (resetValueOnSelectProp(event)) store?.resetValue();
				store?.setSelectedValue((prevValue) => {
					if (!Array.isArray(prevValue)) return value;
					if (prevValue.includes(value)) return prevValue.filter((v) => v !== value);
					return [...prevValue, value];
				});
			}
			if (setValueOnClickProp(event)) store?.setValue(value);
		}
		if (hideOnClickProp(event)) store?.hide();
	});
	const onKeyDownProp = props.onKeyDown;
	const onKeyDown = useEvent((event) => {
		onKeyDownProp?.(event);
		if (event.defaultPrevented) return;
		const baseElement = store?.getState().baseElement;
		if (!baseElement) return;
		if (hasFocus(baseElement)) return;
		const printable = event.key.length === 1 && !event.ctrlKey && !event.metaKey;
		const paste = (!isApple() ? event.ctrlKey : event.metaKey) && event.key.toLowerCase() === "v";
		const deleteKey = event.key === "Backspace" || event.key === "Delete";
		if (printable || paste || deleteKey) {
			queueMicrotask(() => baseElement.focus());
			if (isTextField(baseElement)) store?.setValue(baseElement.value);
		}
	});
	if (multiSelectable && selected != null) props = {
		"aria-selected": selected,
		...props
	};
	props = useWrapElement(props, (element) => /* @__PURE__ */ jsx(ComboboxItemValueContext.Provider, {
		value,
		children: /* @__PURE__ */ jsx(ComboboxItemCheckedContext.Provider, {
			value: selected ?? false,
			children: element
		})
	}), [value, selected]);
	props = {
		role: getItemRole(useContext(ComboboxListRoleContext)),
		children: value,
		...props,
		onClick,
		onKeyDown
	};
	const moveOnKeyPressProp = useBooleanEvent(moveOnKeyPress);
	props = useCompositeItem({
		store,
		...props,
		getItem,
		moveOnKeyPress: (event) => {
			if (!moveOnKeyPressProp(event)) return false;
			const moveEvent = new Event("combobox-item-move");
			(store?.getState().baseElement)?.dispatchEvent(moveEvent);
			return true;
		}
	});
	props = useCompositeHover({
		store,
		focusOnHover,
		...props
	});
	return props;
});
/**
* Renders a combobox item inside
* [`ComboboxList`](https://ariakit.com/reference/combobox-list) or
* [`ComboboxPopover`](https://ariakit.com/reference/combobox-popover)
* components.
*
* By default, the [`value`](https://ariakit.com/reference/combobox-item#value)
* prop will be rendered as the children, but this can be overriden.
* @see https://ariakit.com/components/combobox
* @example
* ```jsx {4-6}
* <ComboboxProvider>
*   <Combobox />
*   <ComboboxPopover>
*     <ComboboxItem value="Apple" />
*     <ComboboxItem value="Banana" />
*     <ComboboxItem value="Orange" />
*   </ComboboxPopover>
* </ComboboxProvider>
* ```
*/
const ComboboxItem = memo(forwardRef(function ComboboxItem(props) {
	return createElement(TagName, useComboboxItem(props));
}));
//#endregion
export { ComboboxItem, getItemRole, useComboboxItem };

//# sourceMappingURL=combobox-item.js.map